// src/features/task/create/TaskTypeSelect.tsx
// Choosing a task type: one click opens the list, one click picks.
//
// The type reshapes the whole form, so it is the property changed most often on
// this screen — and it used to cost two decisions, because the row opened a
// popover that then held the control. This is the plain dropdown that
// arrangement was imitating: a listbox anchored to the row, the chosen type
// ticked, each type's own description carried as its second line, and a filter
// box once the list stops fitting in one glance. Nothing is nested.
//
// Types come from `ResolveTaskContext.allowed_types` — a scope-aware list, not
// a static enum — so loading, failure and "this scope offers none" are all
// states of the list itself and are shown where the list would have been.

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@core/ui';
import { useI18n } from '@react/hooks';
import type { TaskTypeDefinition } from '@core/types/task';
import { AnchoredMenu } from '@/components/AnchoredMenu';
import { FORM_ICONS, Icon } from './icons';
import { propertyTriggerClass, type PropertyTriggerVariant } from './propertyTrigger';
import { typeDefIconPath, typeDefId, typeDefLabelKey } from './logic';

/** Above this count the eye stops scanning and starts hunting — offer a filter. */
const FILTER_LIMIT = 8;

const MENU_MAX_HEIGHT = 340;
const MENU_MIN_WIDTH = 240;

export interface TaskTypeSelectProps {
  types: TaskTypeDefinition[];
  /** Selected definition id (entity id, else slug). */
  value: string;
  onChange: (typeId: string) => void;
  isLoading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  disabled?: boolean;
  /** Trigger look — a panel row, or an inline chip on the compact surface. */
  variant?: PropertyTriggerVariant;
  /** Accessible name of the control, and the chip's text while nothing is set. */
  label: string;
  /** What the `row` variant reads while nothing is chosen. */
  emptyText?: string;
  /** Where "Manage" points. Omitted, the link is not offered. */
  manageHref?: string;
  className?: string;
}

export function TaskTypeSelect({
  types,
  value,
  onChange,
  isLoading,
  error,
  onRetry,
  disabled,
  variant = 'row',
  label,
  emptyText,
  manageHref,
  className,
}: TaskTypeSelectProps) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const menuId = useId();
  const listId = `${menuId}-list`;

  const labelOf = (def: TaskTypeDefinition): string => {
    const key = typeDefLabelKey(def);
    if (key) return t(key);
    return def.display_name || def.slug;
  };

  const selected = types.find((def) => typeDefId(def) === value) || null;
  const filterable = types.length > FILTER_LIMIT;

  const options = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return types;
    return types.filter((def) => {
      const key = typeDefLabelKey(def);
      const name = key ? t(key) : def.display_name || def.slug;
      return (
        name.toLowerCase().includes(needle) ||
        (def.description || '').toLowerCase().includes(needle) ||
        (def.slug || '').toLowerCase().includes(needle)
      );
    });
    // `t` is stable per language; listing it keeps the filter honest after a switch.
  }, [types, query, t]);

  const close = () => {
    setOpen(false);
    setQuery('');
  };

  const closeAndReturnFocus = () => {
    close();
    triggerRef.current?.focus();
  };

  const openList = () => {
    const index = types.findIndex((def) => typeDefId(def) === value);
    setActiveIndex(index >= 0 ? index : 0);
    setQuery('');
    setOpen(true);
  };

  const select = (def: TaskTypeDefinition) => {
    onChange(typeDefId(def));
    closeAndReturnFocus();
  };

  // A filter box owns the caret once it exists; without one the trigger keeps
  // focus and drives the list through aria-activedescendant.
  //
  // A frame later, because the menu spends its first paint hidden while
  // AnchoredMenu measures it — and nothing invisible can take focus.
  useEffect(() => {
    if (!open || !filterable) return;
    const raf = window.requestAnimationFrame(() => searchRef.current?.focus());
    return () => window.cancelAnimationFrame(raf);
  }, [open, filterable]);

  // A new filter means a new best answer, so Enter should take the top match
  // rather than whatever the marker happened to be sitting on.
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Types arriving late can leave the marker past the end of the list.
  useEffect(() => {
    setActiveIndex((prev) => (prev < options.length ? prev : 0));
  }, [options.length]);

  useEffect(() => {
    if (!open) return;
    const item = listRef.current?.children[activeIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [open, activeIndex]);

  /** Shared by the trigger and the filter box — whichever currently has focus. */
  const handleListKeys = (e: ReactKeyboardEvent) => {
    if (disabled) return;
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        if (!open) openList();
        else setActiveIndex((prev) => Math.min(prev + 1, options.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) openList();
        else setActiveIndex((prev) => Math.max(prev - 1, 0));
        break;
      case 'Home':
        if (!open) break;
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        if (!open) break;
        e.preventDefault();
        setActiveIndex(Math.max(0, options.length - 1));
        break;
      case 'Enter':
      case ' ':
        // A filter box must keep the space bar for typing.
        if (e.key === ' ' && open && filterable) break;
        e.preventDefault();
        if (!open) openList();
        else if (options[activeIndex]) select(options[activeIndex]);
        break;
      case 'Escape':
        if (!open) break;
        e.preventDefault();
        closeAndReturnFocus();
        break;
      case 'Tab':
        if (open) close();
        break;
    }
  };

  const activeId = open && options.length > 0 ? `${menuId}-option-${activeIndex}` : undefined;
  const hasValue = Boolean(selected);
  const triggerText = selected ? labelOf(selected) : variant === 'row' ? (emptyText ?? label) : label;

  const list: ReactNode =
    isLoading && types.length === 0 ? (
      <div className="flex flex-col gap-1.5 p-2" aria-busy="true" aria-live="polite">
        <span className="sr-only">{t('task.createForm.loadingTypes')}</span>
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-7 w-full motion-safe:animate-pulse rounded-md bg-muted" />
        ))}
      </div>
    ) : error && types.length === 0 ? (
      <div className="flex flex-col items-start gap-2 p-2">
        <p className="text-xs text-destructive">{error}</p>
        {onRetry && (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-sm text-xs font-medium text-primary underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {t('common.retry')}
          </button>
        )}
      </div>
    ) : options.length === 0 ? (
      <p className="p-2 text-xs text-muted-foreground">
        {types.length === 0 ? t('task.createForm.noTypesAvailable') : t('common.noResults')}
      </p>
    ) : (
      <ul ref={listRef} id={listId} role="listbox" aria-label={label} className="py-1">
        {options.map((def, index) => {
          const id = typeDefId(def);
          const isSelected = id === value;
          const isActive = index === activeIndex;
          return (
            <li
              key={id || index}
              id={`${menuId}-option-${index}`}
              role="option"
              aria-selected={isSelected}
              onMouseEnter={() => setActiveIndex(index)}
              onMouseDown={(e) => {
                // Keep the caret in the filter box; the click is the choice.
                e.preventDefault();
                select(def);
              }}
              className={cn(
                'flex cursor-pointer select-none items-start gap-2 px-2 py-1.5 text-sm',
                isActive && 'bg-accent text-accent-foreground',
                isSelected && !isActive && 'bg-accent/50',
              )}
            >
              {def.color ? (
                <span
                  className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10"
                  style={{ backgroundColor: def.color }}
                  aria-hidden="true"
                />
              ) : (
                <Icon d={typeDefIconPath(def)} className="mt-0.5 h-4 w-4 shrink-0" />
              )}
              <span className="min-w-0 flex-1">
                <span className="block truncate">{labelOf(def)}</span>
                {def.description && (
                  <span className="block truncate text-xs text-muted-foreground" title={def.description}>
                    {def.description}
                  </span>
                )}
              </span>
              {isSelected && <Icon d={FORM_ICONS.check} className="mt-0.5 h-3.5 w-3.5 shrink-0" />}
            </li>
          );
        })}
      </ul>
    );

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        onClick={() => (open ? closeAndReturnFocus() : openList())}
        onKeyDown={handleListKeys}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        // Without a filter box the trigger keeps focus, so it is the element
        // that has to name the active option.
        aria-activedescendant={filterable ? undefined : activeId}
        aria-label={selected ? `${label}: ${labelOf(selected)}` : emptyText ? `${label}: ${emptyText}` : label}
        className={cn(propertyTriggerClass(variant, { hasValue, open }), className)}
      >
        {selected?.color ? (
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full ring-1 ring-inset ring-black/10"
            style={{ backgroundColor: selected.color }}
            aria-hidden="true"
          />
        ) : (
          <Icon d={typeDefIconPath(selected)} className="h-3.5 w-3.5 shrink-0" />
        )}
        <span className={cn('truncate', variant === 'row' && 'min-w-0 flex-1')}>{triggerText}</span>
        <Icon
          d={FORM_ICONS.chevronDown}
          className={cn('h-3.5 w-3.5 shrink-0 opacity-50 transition-transform', open && 'rotate-180')}
        />
      </button>

      <AnchoredMenu
        anchor={triggerRef}
        open={open}
        onClose={close}
        ref={menuRef}
        id={menuId}
        role="none"
        // Escape is handled where focus is, so the trigger gets it back.
        closeOnEscape={false}
        matchTriggerWidth
        minWidth={MENU_MIN_WIDTH}
        maxHeight={MENU_MAX_HEIGHT}
        gap={6}
        // A dropdown is as wide as the thing it drops from, near enough. The
        // cap is what makes `truncate` bite on the descriptions — without a
        // definite width the list would size itself to the longest one and
        // stop reading as a dropdown at all.
        className="max-w-[20rem]"
      >
        {filterable && (
          // Sticky rather than outside the scroller: AnchoredMenu owns the
          // overflow, and a second scroll container inside it is one too many.
          <div className="sticky top-0 z-10 border-b border-border bg-popover p-1.5">
            <div className="relative">
              <Icon
                d={FORM_ICONS.search}
                className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
              />
              <input
                ref={searchRef}
                type="text"
                role="combobox"
                aria-expanded="true"
                aria-controls={listId}
                aria-activedescendant={activeId}
                aria-autocomplete="list"
                aria-label={t('task.createForm.searchTypes')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleListKeys}
                placeholder={t('task.createForm.searchTypes')}
                className="w-full rounded-md border border-input bg-background py-1 pl-7 pr-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>
        )}

        {list}

        {manageHref && (
          <div className="sticky bottom-0 border-t border-border bg-popover px-2 py-1.5">
            <Link
              to={manageHref}
              target="_blank"
              className="rounded-sm text-xs text-muted-foreground underline-offset-2 transition-colors hover:text-primary hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('common.manage')}
            </Link>
          </div>
        )}
      </AnchoredMenu>
    </>
  );
}
